'use client'

import { Menu as MenuPrimitive } from '@base-ui/react/menu'
import { CaretRightIcon, CheckIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / DropdownMenu. Figma: 10 / Components / DropdownMenu.
 * Surface: Colors/surface/overlay, 8px corners, 8px padding, default border.
 * DropdownMenuItem: 40px rows, 20px icon, Roboto / text-base. variant destructive uses Colors/status/error.
 */
const DropdownMenu = MenuPrimitive.Root

function DropdownMenuTrigger({ className, ...props }: Omit<MenuPrimitive.Trigger.Props, 'className'> & { className?: string }) {
    return (
        <MenuPrimitive.Trigger
            data-slot='dropdown-menu-trigger'
            className={className}
            {...props}
        />
    )
}

type DropdownMenuContentProps = Omit<MenuPrimitive.Popup.Props, 'className'> & {
    className?: string
    side?: MenuPrimitive.Positioner.Props['side']
    align?: MenuPrimitive.Positioner.Props['align']
    sideOffset?: number
}

function DropdownMenuContent({ className, side = 'bottom', align = 'start', sideOffset = 4, ...props }: DropdownMenuContentProps) {
    return (
        <MenuPrimitive.Portal>
            <MenuPrimitive.Positioner side={side} align={align} sideOffset={sideOffset} className='z-50'>
                <MenuPrimitive.Popup
                    data-slot='dropdown-menu-content'
                    className={cn(
                        'min-w-56 origin-(--transform-origin) rounded-lg border border-border-default bg-surface-overlay p-2 text-text-primary shadow-popover outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                        className
                    )}
                    {...props}
                />
            </MenuPrimitive.Positioner>
        </MenuPrimitive.Portal>
    )
}

const itemClasses = 'relative flex h-10 w-full cursor-pointer items-center gap-2 rounded-md px-2 text-base text-text-primary outline-none select-none data-disabled:pointer-events-none data-disabled:text-text-disabled data-highlighted:bg-action-secondary-hover dark:data-highlighted:bg-action-secondary-pressed [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:text-icon-secondary'

type DropdownMenuItemProps = Omit<MenuPrimitive.Item.Props, 'className'> & {
    className?: string
    variant?: 'default' | 'destructive'
}

function DropdownMenuItem({ className, variant = 'default', ...props }: DropdownMenuItemProps) {
    return (
        <MenuPrimitive.Item
            data-slot='dropdown-menu-item'
            data-variant={variant}
            className={cn(
                itemClasses,
                variant === 'destructive' && 'text-status-error-foreground [&_svg]:text-status-error-foreground',
                className
            )}
            {...props}
        />
    )
}

function DropdownMenuCheckboxItem({ className, children, ...props }: Omit<MenuPrimitive.CheckboxItem.Props, 'className'> & { className?: string }) {
    return (
        <MenuPrimitive.CheckboxItem
            data-slot='dropdown-menu-checkbox-item'
            className={cn(itemClasses, 'pr-9', className)}
            {...props}
        >
            {children}
            <MenuPrimitive.CheckboxItemIndicator className='absolute right-2 flex size-5 items-center justify-center'>
                <CheckIcon className='text-icon-brand' aria-hidden='true' />
            </MenuPrimitive.CheckboxItemIndicator>
        </MenuPrimitive.CheckboxItem>
    )
}

function DropdownMenuGroup({ className, ...props }: Omit<MenuPrimitive.Group.Props, 'className'> & { className?: string }) {
    return (
        <MenuPrimitive.Group
            data-slot='dropdown-menu-group'
            className={cn('flex flex-col', className)}
            {...props}
        />
    )
}

function DropdownMenuLabel({ className, ...props }: Omit<MenuPrimitive.GroupLabel.Props, 'className'> & { className?: string }) {
    return (
        <MenuPrimitive.GroupLabel
            data-slot='dropdown-menu-label'
            className={cn('px-2 py-1 text-sm font-medium text-text-secondary', className)}
            {...props}
        />
    )
}

function DropdownMenuSeparator({ className, ...props }: Omit<MenuPrimitive.Separator.Props, 'className'> & { className?: string }) {
    return (
        <MenuPrimitive.Separator
            data-slot='dropdown-menu-separator'
            className={cn('my-0 h-px bg-border-subtle', className)}
            {...props}
        />
    )
}

const DropdownMenuSub = MenuPrimitive.SubmenuRoot

function DropdownMenuSubTrigger({ className, children, ...props }: Omit<MenuPrimitive.SubmenuTrigger.Props, 'className'> & { className?: string }) {
    return (
        <MenuPrimitive.SubmenuTrigger
            data-slot='dropdown-menu-sub-trigger'
            className={cn(itemClasses, className)}
            {...props}
        >
            {children}
            <CaretRightIcon className='ml-auto' aria-hidden='true' />
        </MenuPrimitive.SubmenuTrigger>
    )
}

export {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
}
